import { Box, Typography } from '@material-ui/core';
import isEmpty from 'lodash/isEmpty';
import React, { FC, useCallback, useContext, useEffect } from 'react';
import { useFormState } from 'react-final-form';
import { useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { Button, Error, FieldTextInput, NamedLink, ShopifyOrder } from '../../components';
import { useShopConfig } from '../../hooks/shopConfig';
import BackToSearch from './BackToSearch';
import { EditListingPageState } from './EditListingPage.duck';
import { getOrderHistoryDescription, ListingPageParamTab } from './EditListingPage.utils';
import { useListingFlowConfig } from '../../hooks/useListingFlowConfig';
import { useLazyApi } from '../../hooks/useLazyApi';
import AppContext from '../../context/AppContext';
import { useBrandCountryConfig } from '../../hooks/useCountryConfig';
import { fetchOrderByOrderNumber } from '../../util/api';
import { CurrentUser } from '../../types/sharetribe/currentUser';
import { EmailVerificationGate, SignUpToSeeOrderHistory } from './Search';
import TypographyWrapper, {
  TypographyFormat,
} from '../../components/TypographyWrapper/TypographyWrapper';
import { FindItemMethod } from '../../types/sharetribe/listing';

const EMAIL_OR_PHONE_FOR_ORDER = 'emailOrPhoneForOrder';
const ORDER_CONFIRMATION_NUMBER = 'orderConfirmationNumber';

const EmailOrPhoneInput: FC = () => (
  <FieldTextInput
    id={EMAIL_OR_PHONE_FOR_ORDER}
    name={EMAIL_OR_PHONE_FOR_ORDER}
    label="Email or Phone Number"
    placeholder="Enter email or phone number used for order"
  />
);

const OrderNumberInput: FC = () => (
  <FieldTextInput
    id={ORDER_CONFIRMATION_NUMBER}
    name={ORDER_CONFIRMATION_NUMBER}
    label="Order Confirmation Number"
    placeholder="Enter order confirmation number"
  />
);

interface FindButtonProps {
  isDisabled: boolean;
  isLoading: boolean;
  onClick: () => void;
}

const FindButton: FC<FindButtonProps> = (props) => {
  const { isDisabled, isLoading, onClick } = props;

  return (
    <Button inProgress={isLoading} disabled={isDisabled} onClick={onClick} type="button">
      Find Order
    </Button>
  );
};

const Empty: FC = () => {
  const { allowedFindItemMethods } = useListingFlowConfig();

  const { params } = useSelector<any>((state) => state.EditListingPage) as EditListingPageState;
  const shouldAllowCannotFind = allowedFindItemMethods.includes(FindItemMethod.CannotFind);

  if (!params) {
    return null;
  }

  return (
    <Typography variant="body1">
      We couldn’t find any orders that match the given information. Please make sure the email
      address or phone number is the one you used during checkout and that the order number is
      correct.{' '}
      {shouldAllowCannotFind && (
        <>
          You can also{' '}
          <NamedLink
            name="EditListingPage"
            params={{ ...params, tab: ListingPageParamTab.CannotFind }}
          >
            <TypographyWrapper
              variant="body1"
              format={TypographyFormat.Underlined}
              typographyOverrides={{ display: 'inline' }}
            >
              enter your item manually
            </TypographyWrapper>
          </NamedLink>
          .
        </>
      )}
    </Typography>
  );
};

const FindWithOrderNumber: FC = () => {
  const currentUser = useSelector<any>((state) => state.user.currentUser) as
    | CurrentUser
    | undefined;
  const { additionalOrdersQuery, listingFlowConfig, shopName } = useShopConfig();
  const { orderHistoryDescription } = listingFlowConfig;

  const { treetId } = useContext(AppContext);
  const { countryCode } = useBrandCountryConfig();

  const { values } = useFormState();
  const { emailOrPhoneForOrder, orderConfirmationNumber } = values;

  const {
    lazyQuery: lazyFetchOrderByOrderNumber,
    loading: areOrdersByOrderNumberLoading,
    called: orderByOrderNumberCalled,
    error: orderByOrderNumberError,
    data: orderByOrderNumber,
  } = useLazyApi(fetchOrderByOrderNumber);

  const fetchOrder = (emailOrPhone: string, orderNumber: string) => {
    lazyFetchOrderByOrderNumber({
      treetId,
      countryCode,
      additionalOrdersQuery,
      orderNumber,
      emailOrNumber: emailOrPhone,
    });
  };

  // Only query if user has not typed for 0.8 seconds
  const debouncedFetchOrder = useCallback(
    debounce((emailOrPhone, orderNumber) => {
      fetchOrder(emailOrPhone, orderNumber);
    }, 800),
    []
  );

  useEffect(() => {
    if (!emailOrPhoneForOrder || !orderConfirmationNumber) return;
    debouncedFetchOrder(emailOrPhoneForOrder, orderConfirmationNumber);
  }, [emailOrPhoneForOrder, orderConfirmationNumber]);

  const handleFindClick = () => debouncedFetchOrder(emailOrPhoneForOrder, orderConfirmationNumber);

  const orders = orderByOrderNumber?.customers?.edges?.[0]?.node?.orders?.edges;
  const hasNoOrders = isEmpty(orders) && orderByOrderNumberCalled && !areOrdersByOrderNumberLoading;

  return (
    <>
      <Box py={1}>
        <BackToSearch />
      </Box>
      {currentUser && (
        <EmailVerificationGate>
          <>
            <Typography variant="body1" style={{ paddingTop: '12px' }}>
              {getOrderHistoryDescription(orderHistoryDescription)}
            </Typography>
            <Box py={2}>
              <EmailOrPhoneInput />
            </Box>
            <Box py={2}>
              <OrderNumberInput />
            </Box>
            <Box py={4}>
              <FindButton
                isDisabled={!emailOrPhoneForOrder || !orderConfirmationNumber}
                isLoading={areOrdersByOrderNumberLoading}
                onClick={handleFindClick}
              />
            </Box>
            {orderByOrderNumberError && (
              <Error>
                There was an error fetching your order. Please try again or use a different method
                to find your item.
              </Error>
            )}
            {hasNoOrders && <Empty />}
            {!isEmpty(orders) && (
              <ShopifyOrder
                order={orders[0]}
                findItemMethod={FindItemMethod.OrderNumber}
                emailOrPhoneForOrder={emailOrPhoneForOrder}
                orderConfirmationNumber={orderConfirmationNumber}
              />
            )}
          </>
        </EmailVerificationGate>
      )}

      {!currentUser && <SignUpToSeeOrderHistory shopName={shopName} />}
    </>
  );
};

export default FindWithOrderNumber;
